import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

// Cute animal cartoon avatars — emoji rendered on pastel-coloured circles.
// Self-contained SVG data URIs, no external API dependency.
const ANIMALS = [
  { id: 'monkey',   emoji: '🐵', bg: '#FFE4B5', name: 'Monkey' },
  { id: 'lion',     emoji: '🦁', bg: '#FED7AA', name: 'Lion' },
  { id: 'tiger',    emoji: '🐯', bg: '#FECACA', name: 'Tiger' },
  { id: 'elephant', emoji: '🐘', bg: '#E0E7FF', name: 'Elephant' },
  { id: 'giraffe',  emoji: '🦒', bg: '#FEF3C7', name: 'Giraffe' },
  { id: 'panda',    emoji: '🐼', bg: '#F1F5F9', name: 'Panda' },
  { id: 'koala',    emoji: '🐨', bg: '#E2E8F0', name: 'Koala' },
  { id: 'fox',      emoji: '🦊', bg: '#FED7AA', name: 'Fox' },
  { id: 'rabbit',   emoji: '🐰', bg: '#FFE4E6', name: 'Rabbit' },
  { id: 'bear',     emoji: '🐻', bg: '#FEF3C7', name: 'Bear' },
  { id: 'pig',      emoji: '🐷', bg: '#FCE7F3', name: 'Pig' },
  { id: 'frog',     emoji: '🐸', bg: '#DCFCE7', name: 'Frog' },
  { id: 'penguin',  emoji: '🐧', bg: '#DBEAFE', name: 'Penguin' },
  { id: 'zebra',    emoji: '🦓', bg: '#F1F5F9', name: 'Zebra' },
  { id: 'unicorn',  emoji: '🦄', bg: '#F3E8FF', name: 'Unicorn' },
  { id: 'owl',      emoji: '🦉', bg: '#FEF3C7', name: 'Owl' },
];

function buildAnimalAvatar({ emoji, bg }) {
  // SVG rendered as data URI — escape `#` for proper URL encoding.
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>
    <circle cx='50' cy='50' r='50' fill='${bg}'/>
    <text x='50' y='72' font-size='66' text-anchor='middle' font-family='Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif'>${emoji}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${svg.replace(/\n\s*/g, '').replace(/#/g, '%23')}`;
}

export const AVATAR_PRESETS = ANIMALS.map((a) => ({
  id: a.id,
  name: a.name,
  url: buildAnimalAvatar(a),
}));

/**
 * AvatarPicker — horizontal scrolling gallery of cute animal cartoon avatars.
 */
export default function AvatarPicker({ value, onChange, size = 'md' }) {
  const dim = size === 'sm' ? 'w-12 h-12' : 'w-14 h-14';

  return (
    <div className="space-y-2 w-full min-w-0" data-testid="avatar-picker">
      <div className="text-xs font-nunito font-semibold text-slate-600">
        Pick an animal avatar <span className="text-slate-400 font-normal">(optional)</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin w-full min-w-0">
        {AVATAR_PRESETS.map((p) => {
          const selected = value === p.url;
          return (
            <motion.button
              key={p.id}
              type="button"
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onChange(selected ? null : p.url)}
              className={`relative ${dim} flex-shrink-0 rounded-full overflow-hidden ring-2 transition-all ${
                selected
                  ? 'ring-[#FD5E53] ring-offset-2 ring-offset-white shadow-md'
                  : 'ring-slate-200 hover:ring-[#38BDF8]'
              }`}
              data-testid={`avatar-option-${p.id}`}
              aria-label={`${p.name} avatar`}
              title={p.name}
            >
              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
              {selected && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute inset-0 bg-[#FD5E53]/25 flex items-center justify-center"
                >
                  <Check className="w-5 h-5 text-white drop-shadow" strokeWidth={3} />
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
